import { Card, Eyebrow, Spinner, formatDate, money } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, StatusBadge } from '../components/DataTable.js';
import { useResource } from '../lib/useResource.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

/**
 * What this operator owes the platform (docs/06 "/billing", docs/08 §2).
 * Read-only and deliberately the same figures the platform will invoice — the
 * point is that there are no surprises at month end.
 */

interface Invoice {
  id: string;
  number: string;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  currency: string;
  status: string;
  issuedAt: string | null;
  paidAt: string | null;
}

interface BillingPayload {
  contract: {
    type: 'COMMISSION' | 'MONTHLY' | 'HYBRID';
    commissionBps: number | null;
    monthlyFeeCents: number | null;
    currency: string;
    effectiveFrom: string;
  } | null;
  periodStart: string;
  accrual: {
    bookings: number;
    refunds: number;
    grossCents: number;
    commissionCents: number;
    monthlyFeeCents: number;
  };
  invoices: Invoice[];
}

export function Billing() {
  const t = useT();
  const { tenant } = useSession();
  const timezone = tenant?.timezone ?? 'UTC';
  const billing = useResource<BillingPayload>('/admin/billing');

  if (billing.loading) {
    return (
      <Page title={t('nav.billing')}>
        <div className="grid place-items-center py-16">
          <Spinner label="Loading" />
        </div>
      </Page>
    );
  }
  if (!billing.data) {
    return (
      <Page title={t('nav.billing')}>
        <p style={{ color: 'var(--signal-cancelled)' }}>{billing.error}</p>
      </Page>
    );
  }

  const { contract, accrual, invoices, periodStart } = billing.data;
  const currency = contract?.currency ?? tenant?.currency ?? 'EUR';
  const runningTotal =
    accrual.commissionCents + (contract?.type === 'COMMISSION' ? 0 : accrual.monthlyFeeCents);

  const columns: Column<Invoice>[] = [
    { key: 'number', header: t('f.invoice'), render: (row) => <span className="tabular font-medium">{row.number}</span> },
    {
      key: 'period',
      header: t('f.period'),
      render: (row) => (
        <span className="tabular">
          {formatDate(row.periodStart, timezone)} – {formatDate(row.periodEnd, timezone)}
        </span>
      ),
    },
    { key: 'total', header: t('f.total'), numeric: true, render: (row) => money(row.totalCents, row.currency) },
    { key: 'status', header: t('f.status'), render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <Page title={t('nav.billing')} description={t('bl.subtitle')}>
      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <Eyebrow>{t('bl.yourContract')}</Eyebrow>
          {contract ? (
            <dl className="mt-3 flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <dt style={{ color: 'var(--color-muted)' }}>{t('f.type')}</dt>
                <dd className="font-medium">{contract.type.toLowerCase()}</dd>
              </div>
              {contract.commissionBps !== null && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--color-muted)' }}>{t('bl.commissionPer')}</dt>
                  <dd className="tabular font-medium">{(contract.commissionBps / 100).toFixed(2)}%</dd>
                </div>
              )}
              {contract.monthlyFeeCents !== null && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--color-muted)' }}>{t('bl.monthlyFee')}</dt>
                  <dd className="money font-medium">{money(contract.monthlyFeeCents, currency)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt style={{ color: 'var(--color-muted)' }}>{t('bl.inEffect')}</dt>
                <dd className="tabular font-medium">{formatDate(contract.effectiveFrom, timezone)}</dd>
              </div>
            </dl>
          ) : (
            <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
              {t('bl.noContract')}
            </p>
          )}
        </Card>

        <Card>
          <Eyebrow>{t('bl.thisMonth')}</Eyebrow>
          <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
            Since {formatDate(periodStart, timezone)}
          </p>
          <dl className="mt-3 flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>{t('bl.confirmed')}</dt>
              <dd className="tabular font-medium">{accrual.bookings}</dd>
            </div>
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>{t('st.refunded')}</dt>
              <dd className="tabular font-medium">{accrual.refunds}</dd>
            </div>
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>{t('bl.gross')}</dt>
              <dd className="money font-medium">{money(accrual.grossCents, currency)}</dd>
            </div>
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>{t('bl.accrued')}</dt>
              <dd className="money font-medium">{money(accrual.commissionCents, currency)}</dd>
            </div>
            {contract?.type !== 'COMMISSION' && accrual.monthlyFeeCents > 0 && (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--color-muted)' }}>{t('bl.monthlyFee')}</dt>
                <dd className="money font-medium">{money(accrual.monthlyFeeCents, currency)}</dd>
              </div>
            )}
            <div
              className="mt-1 flex justify-between border-t pt-2"
              style={{ borderColor: 'var(--color-line)' }}
            >
              <dt className="font-semibold">{t('bl.estimated')}</dt>
              <dd className="money font-semibold">{money(runningTotal, currency)}</dd>
            </div>
          </dl>
          <p className="mt-2 text-xs" style={{ color: 'var(--color-faint)' }}>
            {t('bl.refundNote')}
          </p>
        </Card>
      </div>

      <section className="mt-7">
        <h2 className="mb-2 text-sm font-semibold">{t('f.invoices')}</h2>
        <DataTable
          columns={columns}
          rows={invoices}
          rowKey={(row) => row.id}
          empty={{
            title: t('bl.noInvoices'),
            description: t('bl.invoiceHint'),
          }}
        />
      </section>
    </Page>
  );
}
